import {
  AbilityBuilder,
  ForcedSubject,
  Ability,
  AbilityClass,
} from '@casl/ability';

import { UserRoleEnum } from '../types';

type Actions = ['manage', 'create', 'edit', 'cancel', 'read', 'printFlow'];
type Subjects = [
  'Employees',
  'Dashboard',
  'Absences',
  'Candidates',
  'Candidate',
  'Events',
  'EventTypes',
  'EventBatch',
  'PaperEvents',
  'User',
  'Policy',
  'Organization',
  'ProcessCatalog',
  'Competencies',
  'all',
  'Settings',
  'CompanyProfiles',
  'SettingsAdmin',
  'SettingsAdmin',
  'Attorneys',
  'Vacations',
  'VacationsSettings',
  'Payslips',
  'Personal',
  'SmevErrors',
  'AccountDashboardSettings',
  'AccountAccessSettings',
  'AccountGeneralSettings',
  'GeneralNews',
  'SocialServices',
  'CompanyServices',
  'EmployeeServices',
  'News',
  'Search',
  'Survey',
  'TargetAudience',
  'Quiz',
  'Categories',
  'Surveys',
  'Community',
  'Favorites',
  'Feed',
  'SocialLife',
  'SocialAccess',
  'Review',
  'AccountSettings',
  'AccountRoleModelSettings',
  'WidgetsContentSettings',
  'BusinessTrips',
];

export type AppAbilities = [
  Actions[number],
  Subjects[number] | ForcedSubject<Exclude<Subjects[number], 'all'>>,
];

export type Resources = AppAbilities[1];
export type AppAbility = Ability<AppAbilities>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AppAbility = Ability as AbilityClass<AppAbility>;

/** @knipignore */
export type AbilityConfig = {
  role: string;
  userRepresentative: boolean;
  canSeeEvents: boolean;
  canSeeEventsToPaper: boolean;
  canSeeEmployees: boolean;
  canSeePolicies: boolean;
  canSeeCompany: boolean;
  canSeeCompanyProfiles: boolean;
  canSeeSettings: boolean;
  canSeeCandidates: boolean;
  canSeeAttorneys: boolean;
  canSeeVacations: boolean;
  canSeeVacationsSettings: boolean;
  canSeePayslips: boolean;
  canSeeGoalManagement: boolean;
  canSeeSmevErrors: boolean;
  canSeeAbsences: boolean;
  canSeeCompetencies: boolean;
  canSeePersonalProfile: boolean;
  canSeeUnits: boolean;
  canSeeNews: boolean;
  canSeeTargetAudience: boolean;
  canSeeSocialCategories: boolean;
  canSeeSocialAccess: boolean;
  canSeeSocialSurveys: boolean;
  canSeeSocialCommunities: boolean;
  canSeeSocialFeed: boolean;
  canSeeAccountSettings: boolean;
  canSeeAccountDashboardSettings: boolean;
  canSeeAccountAccessSettings: boolean;
  canSeeAccountGeneralSettings: boolean;
  canSeeSocialWidgetContent: boolean;
  canSeeBusinessTrips: boolean;
  canSeeProcessCatalog: boolean;
  canSeeFavorites: boolean;
  canSeeQuickSearch: boolean;
};

export default function defineAbilityFor({
  role,
  userRepresentative,
  canSeeEvents,
  canSeeEventsToPaper,
  canSeeEmployees,
  canSeePolicies,
  canSeeCompany,
  canSeeCompanyProfiles,
  canSeeSettings,
  canSeeCandidates,
  canSeeAttorneys,
  canSeeVacations,
  canSeeVacationsSettings,
  canSeePayslips,
  canSeeGoalManagement,
  canSeeSmevErrors,
  canSeeAbsences,
  canSeeCompetencies,
  canSeePersonalProfile,
  canSeeUnits,
  canSeeNews,
  canSeeTargetAudience,
  canSeeSocialCategories,
  canSeeSocialAccess,
  canSeeSocialSurveys,
  canSeeSocialCommunities,
  canSeeSocialFeed,
  canSeeAccountSettings,
  canSeeAccountDashboardSettings,
  canSeeAccountAccessSettings,
  canSeeAccountGeneralSettings,
  canSeeSocialWidgetContent,
  canSeeBusinessTrips,
  canSeeProcessCatalog,
  canSeeFavorites,
  canSeeQuickSearch,
}: AbilityConfig) {
  const { can, cannot, build } = new AbilityBuilder(AppAbility);

  can('manage', 'all');

  if (role === UserRoleEnum.Employee) {
    cannot('read', 'Employees');
    cannot('cancel', 'Events');
    cannot('create', 'EventTypes');
    cannot('read', 'Candidates');
    cannot('create', 'Candidates');
    cannot('read', 'PaperEvents');
    cannot('create', 'Policy');
    cannot('edit', 'Policy');
    cannot('create', 'Organization');
    cannot('edit', 'Organization');
    cannot('read', 'Settings');
    cannot('read', 'CompanyProfiles');
    cannot('read', 'CompanyServices');
    cannot('read', 'Attorneys');
    cannot('read', 'SettingsAdmin');
    cannot('printFlow', 'Events');
    cannot('read', 'News');
    cannot('read', 'Surveys');
    cannot('read', 'TargetAudience');
    cannot('read', 'Community');
  }

  if (role === UserRoleEnum.Company) {
    cannot('read', 'Competencies');
  }

  if (!canSeeUnits) {
    cannot('read', 'Organization');
  }

  if (!canSeeEmployees) {
    cannot('read', 'Employees');
  }

  if (!canSeeEvents) {
    cannot('read', 'Events');
  }

  if (!canSeeEventsToPaper) {
    cannot('read', 'PaperEvents');
  }

  if (!canSeePolicies) {
    cannot('read', 'Policy');
  }

  if (!canSeeCompany) {
    cannot('create', 'Organization');
    cannot('edit', 'Organization');
  }

  if (!canSeeCandidates) {
    cannot('read', 'Candidates');
    cannot('create', 'Candidates');
  }

  if (!canSeeVacations) {
    cannot('read', 'Vacations');
  }

  if (!canSeeVacationsSettings) {
    cannot('read', 'VacationsSettings');
  }

  if (!canSeeSmevErrors) {
    cannot('read', 'SmevErrors');
  }

  if (!canSeePayslips) {
    cannot('read', 'Payslips');
  }

  if (!canSeeGoalManagement) {
    cannot('read', 'Review');
  }

  if (!canSeeFavorites) {
    cannot('read', 'Favorites');
  }

  if (!canSeeQuickSearch) {
    cannot('read', 'Search');
  }

  if (!canSeeBusinessTrips) {
    cannot('read', 'BusinessTrips');
  }

  if (role === UserRoleEnum.Company) {
    cannot('read', 'Personal');
  }

  if (
    canSeeSettings ||
    canSeeAttorneys ||
    canSeeVacationsSettings ||
    canSeeAccountSettings ||
    canSeeSocialWidgetContent
  ) {
    can('read', 'Settings');

    if (!canSeeSettings) {
      cannot('read', 'SettingsAdmin');
    }

    if (!canSeeAttorneys) {
      cannot('read', 'Attorneys');
    }

    if (!canSeeVacationsSettings) {
      cannot('read', 'VacationsSettings');
    }

    if (!canSeeAccountSettings) {
      cannot('read', 'AccountSettings');
    }

    if (!canSeeSocialWidgetContent) {
      cannot('read', 'WidgetsContentSettings');
    }
  } else {
    cannot('read', 'Settings');
    cannot('read', 'CompanyProfiles');
    cannot('read', 'SettingsAdmin');
    cannot('read', 'Attorneys');
    cannot('read', 'SettingsAdmin');
    cannot('read', 'VacationsSettings');
    cannot('read', 'AccountSettings');
  }

  if (!canSeeCompanyProfiles) {
    cannot('read', 'CompanyProfiles');
  }

  if (!canSeeAbsences) {
    cannot('read', 'Absences');
  }

  if (!canSeeCompetencies) {
    cannot('read', 'Competencies');
  }

  if (!canSeePersonalProfile) {
    cannot('read', 'Personal');
  }

  if (!canSeeAccountDashboardSettings) {
    cannot('read', 'AccountDashboardSettings');
  }

  if (!canSeeAccountAccessSettings) {
    cannot('read', 'AccountAccessSettings');
  }

  if (!canSeeAccountGeneralSettings) {
    cannot('read', 'AccountGeneralSettings');
  }

  if (!canSeeProcessCatalog) {
    cannot('read', 'ProcessCatalog');
  }

  if (
    canSeeNews ||
    canSeeSocialCategories ||
    canSeeSocialSurveys ||
    canSeeSocialAccess ||
    canSeeTargetAudience
  ) {
    can('read', 'SocialServices');

    if (!canSeeTargetAudience) {
      cannot('read', 'TargetAudience');
    }

    if (!canSeeNews) {
      cannot('read', 'News');
      cannot('read', 'GeneralNews');
    }

    if (!canSeeSocialCategories) {
      cannot('read', 'Categories');
    }

    if (!canSeeSocialAccess) {
      cannot('read', 'SocialAccess');
    }

    if (role === UserRoleEnum.Employee || !canSeeSocialSurveys) {
      cannot('read', 'Quiz');
      cannot('read', 'Surveys');
    }
  } else {
    cannot('read', 'SocialServices');
    cannot('read', 'News');
    cannot('read', 'Surveys');
    cannot('read', 'GeneralNews');
    cannot('read', 'Quiz');
    cannot('read', 'Categories');
    cannot('read', 'SocialAccess');
    cannot('read', 'TargetAudience');
  }

  if (role === UserRoleEnum.Employee || !canSeeTargetAudience) {
    cannot('read', 'TargetAudience');
  }

  if (role === UserRoleEnum.Employee || !canSeeSocialSurveys) {
    cannot('read', 'Surveys');
    cannot('read', 'Quiz');
  }

  if (role === UserRoleEnum.Employee || !canSeeSocialCommunities) {
    cannot('read', 'Community');
  }

  if (role === UserRoleEnum.Employee || !canSeeSocialFeed) {
    cannot('read', 'Feed');
  }

  if (window.REACT_APP_VKHRTEK_LK_DISABLED && !userRepresentative) {
    cannot('manage', 'all');
    cannot('read', 'Settings');
  }

  return build();
}
